<template>
  <v-card>
    <slot name="header" />
    <info-box
      v-if="!analyticsImpressionLoading && !isLoading && analyticsImpression?.overallTotal === 0"
      :info-message="t('dashboard.details.no-data')"
    />
    <div v-else>
      <Loader v-if="analyticsImpressionLoading" />
      <div v-else>
        <v-card-text class="mt-1 mb-0 pb-0">
          <div class="d-md-flex justify-space-between card-text-wrapper">
            <div style="display: grid">
              <Info
                :number="analyticsImpression?.overallTotal?.toLocaleString() || 0"
                :description="t('dashboard.details.impressions.description-overall')"
              />
              <Info
                :number="analyticsImpression?.last30DaysTotal?.toLocaleString() || 0"
                :description="t('dashboard.details.impressions.description')"
              />
            </div>

            <BarChart
              :config="chartConfig"
              :series="chartSeries"
            />
          </div>
        </v-card-text>
        <Loader
          class="pt-5"
          style="height: 190px"
          size="100"
          v-if="isLoading"
        />
        <div v-else>
          <v-card-text :class="{ 'pb-0': $vuetify.display.mdAndDown }">
            <h4 class="mb-2">
              <span v-if="variables.input.specificDate">
                {{ t('dashboard.details.impressions.sub-title') }}:
                {{ formatDate(variables.input.specificDate, 'namedWeekdayWithYear', $i18n.locale) }}
              </span>
              <span v-else>{{ t('dashboard.details.impressions.main-title') }}</span>
            </h4>
            <div
              class="d-md-flex justify-flex-start flex-wrap"
              style="gap: 12px"
            >
              <Box
                v-for="trafficByUtmSource in totalTrafficByUtmSourceForCompany"
                :key="trafficByUtmSource.title"
                :title="trafficByUtmSource.title"
                :items="trafficByUtmSource.values"
                :width="$vuetify.display.mdAndDown ? '100%' : '19%'"
              />
              <Box
                v-if="!totalTrafficByUtmSourceForCompany?.length"
                :title="t('dashboard.details.impressions.utm-params.title')"
                :items="[]"
                :width="$vuetify.display.mdAndDown ? '100%' : '19%'"
              />
              <br v-if="$vuetify.display.mdAndUp" />
            </div>
          </v-card-text>
          <v-card-text class="pt-0">
            <div class="d-md-flex justify-start">
              <Box
                class="mr-md-4"
                :title="t('dashboard.details.impressions.traffic-source.title')"
                :items="totalTrafficBySourceForCompany"
                :width="$vuetify.display.mdAndDown ? '100%' : '39%'"
              />
            </div>
          </v-card-text>
        </div>
      </div>
    </div>
    <slot name="footer" />
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import InfoBox from '@/components/InfoBox.vue'
import { capitalizeFirstLetter } from '@/lib/capitalize'
import { createBarChartConfig } from '@/modules/dashboard/dashboardDetails/lib/barChartConfig'
import { createBarChartSeries } from '@/modules/dashboard/dashboardDetails/lib/barChartSeries'
import BarChart from '@/modules/dashboard/dashboardDetails/shared/barChart.vue'
import Box from '@/modules/dashboard/dashboardDetails/shared/box.vue'
import Info from '@/modules/dashboard/dashboardDetails/shared/info.vue'
import Loader from '@/modules/dashboard/dashboardDetails/shared/loader.vue'
import GET_ANALYTICS_IMPRESSIONS from '@/modules/dashboard/GetAnalyticsImpressionForCompany.gql'
import TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY from '@/modules/leads/queries/TotalTrafficBySourceForCompany.gql'
import TOTAL_TRAFFIC_BY_UTM_SOURCE_FOR_COMPANY from '@/modules/leads/queries/TotalTrafficByUtmSourceForCompany.gql'

import { formatDate } from '../../../lib/dateTime'

const { t } = useI18n()

const lastSelectedDate = ref(null)

const route = useRoute()
const routeParamId = computed(() => route?.params?.id || null)
const isMasterView = computed(() => route.path.startsWith('/master'))
const { result: analytics, loading: analyticsImpressionLoading } = useQuery(
  GET_ANALYTICS_IMPRESSIONS,
  {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
  }
)

const analyticsImpression = computed(() => analytics.value?.analyticsImpression)

const variables = reactive({
  input: {
    specificDate: null,
    last30Days: false,
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
  },
})

const { result: totalTrafficBySource, loading: trafficSourceLoading } = useQuery(
  TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY,
  variables
)

const directSources = computed(() => {
  const total = totalTrafficBySource.value?.totalTrafficBySourceForCompany?.find(
    (item) => !item.source
  )

  return total ? { label: t('dashboard.details.impressions.utm-direct'), value: total.count } : null
})

const totalTrafficBySourceForCompany = computed(() => {
  const readableItems =
    totalTrafficBySource.value?.totalTrafficBySourceForCompany
      ?.filter((item) => item.source)
      .map((item) => ({
        label: capitalizeFirstLetter(item.source),
        value: item.count,
      })) || []

  const result = [...readableItems]
  if (directSources.value) {
    result.push(directSources.value)
  }

  return result.sort((a, b) => b.value - a.value)
})
const { result: totalTrafficByUtmSource, loading: utmSourceLoading } = useQuery(
  TOTAL_TRAFFIC_BY_UTM_SOURCE_FOR_COMPANY,
  variables
)

const totalTrafficByUtmSourceForCompany = computed(() =>
  totalTrafficByUtmSource.value?.totalTrafficByUtmSourceForCompany
    .map((item) => {
      return {
        title: 'utm-' + item.utmKey,
        values: item.values.map((value) => ({
          label: value.label,
          value: value.count,
        })),
      }
    })
    .sort((a, b) => {
      const sum = (arr) => arr.reduce((acc, v) => acc + v.value, 0)
      return sum(b.values) - sum(a.values)
    })
)

const isLoading = computed(() => trafficSourceLoading.value || utmSourceLoading.value)

const handleSelect = (date) => {
  if (date === lastSelectedDate.value) {
    variables.input.specificDate = null
    lastSelectedDate.value = null
    return
  }

  variables.input.specificDate = date
  lastSelectedDate.value = date
}

const chartConfig = computed(() => {
  return createBarChartConfig({
    chartSeries: chartSeries,
    tooltipText: 'dashboard.details.impressions.chart.tooltip',
    onSelect: handleSelect,
  })
})

const chartSeries = computed(() => {
  return createBarChartSeries(analyticsImpression.value?.specificDateTotals, 'count')
})
</script>
<style scoped>
.card-text-wrapper {
  border-radius: 10px;
  box-shadow: 0 11px 15px 0 rgba(0, 0, 0, 0.04);
}
</style>
